import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useRaceData } from './hooks/useRaceData';
import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthContext';
import { fetchProfile } from './services/profileService';
import MeetingList from './components/MeetingList';
import ResultsHistory from './components/ResultsHistory';
import OpenBets from './components/OpenBets';
import Performance from './components/Performance';
import LandingPage from './components/LandingPage';
import PublicTrackRecordPage from './components/PublicTrackRecordPage';
import { BETFAIR_LANDING_VARIANT } from './components/landingVariants';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ResumeSignupPage from './components/auth/ResumeSignupPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SubscriptionRoute from './components/auth/SubscriptionRoute';
import AdminRoute from './components/auth/AdminRoute';
import AdminPage from './components/AdminPage';
import { checkAdminAccess } from './services/adminService';
import { SeoDefaults } from './components/SeoDefaults';
import SubscribePage from './components/SubscribePage';
import MarketingCheckoutStart from './components/MarketingCheckoutStart';
import ConnectBetfairPage from './components/betfair/ConnectBetfairPage';
import Settings from './components/Settings';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import RefundPolicy from './components/RefundPolicy';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import { HelmetProvider } from 'react-helmet-async';
import AppFooter from './components/AppFooter';
import HelpLayout from './components/guide/HelpLayout';
import {
  GuideGettingStarted,
  GuideAccountSubscription,
  GuideBetfairAiMode,
  GuideRacesAndBetting,
  GuidePerformanceHistory,
  GuideSettingsDigest,
  GuideTroubleshooting,
} from './components/guide/GuidePageViews';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import SleepingHorseEmptyState from './components/SleepingHorseEmptyState';
import ChatWidget from './components/ChatWidget';
import NotifyProvider from './app/notify/NotifyProvider';
import TrialEndingBanner from './components/TrialEndingBanner';
import './App.css';
import './NoRaces.css';
import './N8nChat.css';
import pkg from '../package.json';

const API_URL = process.env.REACT_APP_API_URL || '';

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10));
  const pb = b.split('.').map((x) => parseInt(x, 10));
  for (let i = 0; i < 3; i++) {
    const da = pa[i] || 0;
    const db = pb[i] || 0;
    if (da > db) return 1;
    if (da < db) return -1;
  }
  return 0;
}

function Navigation() {
  const location = useLocation();
  const { user, signOut, session } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const avatarDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user avatar and display name from profile
  useEffect(() => {
    const loadProfile = async () => {
      if (user?.id) {
        try {
          const profile = await fetchProfile(user.id);
          if (profile) {
            setUserAvatar(profile.avatar || null);
            setDisplayName(profile.display_name || null);
          } else {
            setUserAvatar(null);
            setDisplayName(null);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      }
    };
    loadProfile();
  }, [user, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const verifyAdmin = async () => {
      if (!user?.id || !session?.access_token) {
        if (!cancelled) setIsAdmin(false);
        return;
      }

      const allowed = await checkAdminAccess(session.access_token);
      if (!cancelled) setIsAdmin(allowed);
    };

    void verifyAdmin();

    return () => {
      cancelled = true;
    };
  }, [user?.id, session?.access_token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target as Node)) {
        setIsAvatarDropdownOpen(false);
      }
    };

    if (isAvatarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAvatarDropdownOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsMobileMenuOpen(false);
    setIsAvatarDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleAvatarDropdown = () => {
    setIsAvatarDropdownOpen(!isAvatarDropdownOpen);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setIsAvatarDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setIsAvatarDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };
  
  return (
    <>
      <nav className="app-navigation">
        <div className="nav-container">
          <div className="nav-left">
            <img src="/white_wide_logo_noBG.png" alt="AI Horse Racing" className="nav-logo" />
            <div className="nav-links">
              <Link 
                to="/dashboard" 
                className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
              >
                Dashboard
              </Link>
              <Link 
                to="/today-races" 
                className={`nav-link ${location.pathname === '/today-races' ? 'active' : ''}`}
              >
                Today's Races
              </Link>
              <Link 
                to="/today-bets" 
                className={`nav-link ${location.pathname === '/today-bets' || location.pathname === '/open-bets' ? 'active' : ''}`}
              >
                Today's Bets
              </Link>
              <Link 
                to="/performance" 
                className={`nav-link ${location.pathname === '/performance' ? 'active' : ''}`}
              >
                Performance
              </Link>
              <Link 
                to="/results" 
                className={`nav-link ${location.pathname === '/results' ? 'active' : ''}`}
              >
                Results History
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`}
                >
                  Admin
                </Link>
              )}
            </div>
          </div>
          {user && (
            <div className="nav-right">
              <div className="avatar-dropdown" ref={avatarDropdownRef}>
                <button 
                  onClick={toggleAvatarDropdown}
                  className="avatar-dropdown-trigger"
                  aria-label="User menu"
                >
                  <img 
                    src={userAvatar ? `/avatars/${userAvatar}` : '/avatars/1.png'} 
                    alt="User avatar" 
                    className="nav-avatar"
                  />
                  {displayName && (
                    <span className="nav-display-name">{displayName}</span>
                  )}
                  <ChevronDown 
                    size={16} 
                    className={`avatar-chevron ${isAvatarDropdownOpen ? 'open' : ''}`}
                  />
                </button>
                {isAvatarDropdownOpen && (
                  <div className="avatar-dropdown-menu">
                    <button 
                      onClick={handleSettingsClick}
                      className="avatar-dropdown-item"
                    >
                      Settings
                    </button>
                    {isAdmin && (
                      <button
                        onClick={handleAdminClick}
                        className="avatar-dropdown-item"
                      >
                        Admin
                      </button>
                    )}
                    <button 
                      onClick={handleSignOut}
                      className="avatar-dropdown-item"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="mobile-nav">
        <div className="mobile-header">
          <img src="/white_wide_logo_noBG.png" alt="AI Horse Racing" className="mobile-logo" />
        </div>
        <button 
          className="hamburger-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            {user && (
              <div className="mobile-user-info">
                <span className="mobile-user-email">{user.email}</span>
              </div>
            )}
            
            <div className="mobile-nav-links">
              <Link 
                to="/dashboard" 
                className={`mobile-nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Dashboard
              </Link>
              <Link 
                to="/today-races" 
                className={`mobile-nav-link ${location.pathname === '/today-races' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Today's Races
              </Link>
              <Link 
                to="/today-bets" 
                className={`mobile-nav-link ${location.pathname === '/today-bets' || location.pathname === '/open-bets' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Today's Bets
              </Link>
              <Link 
                to="/performance" 
                className={`mobile-nav-link ${location.pathname === '/performance' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Performance
              </Link>
              <Link 
                to="/results" 
                className={`mobile-nav-link ${location.pathname === '/results' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Results History
              </Link>
              <Link 
                to="/settings" 
                className={`mobile-nav-link ${location.pathname === '/settings' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Settings
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`mobile-nav-link ${location.pathname === '/admin' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  Admin
                </Link>
              )}
            </div>

            {user && (
              <button onClick={handleSignOut} className="mobile-sign-out-btn">
                Sign Out
              </button>
            )}
          </div>
        </div>
      )}
      <TrialEndingBanner />
    </>
  );
}

function TodayRaces() {
  const { meetings, runners, loading, error, isEmpty, refetch } = useRaceData();

  if (loading) return <LoadingSpinner />;
  if (isEmpty) return <SleepingHorseEmptyState />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  return <MeetingList meetings={meetings} runners={runners} />;
}

function App() {
  const [updateRequired, setUpdateRequired] = useState(false);
  const [minVersion, setMinVersion] = useState<string | null>(null);

  useEffect(() => {
    const checkVersion = async () => {
      if (!API_URL) return;
      try {
        const resp = await fetch(`${API_URL}/api/meta`);
        if (!resp.ok) return;
        const data = await resp.json();
        const min = typeof data?.minFrontendVersion === 'string' ? data.minFrontendVersion : null;
        if (!min) return;
        setMinVersion(min);
        const current = ((pkg as any).version as string) || '0.0.0';
        if (compareSemver(current, min) < 0) {
          setUpdateRequired(true);
        }
      } catch {
        // If meta is unreachable, don't block access.
      }
    };
    void checkVersion();
  }, []);

  return (
    <AuthProvider>
      <HelmetProvider>
      <Router>
        <div className="App">
          {updateRequired && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: 16,
            }}>
              <div style={{
                maxWidth: 520,
                width: '100%',
                background: '#111827',
                color: '#fff',
                borderRadius: 12,
                padding: 20,
                border: '1px solid rgba(255,255,255,0.12)',
              }}>
                <h2 style={{ marginTop: 0 }}>Update required</h2>
                <p style={{ marginBottom: 12 }}>
                  A newer version of GallopAI is required to continue.
                </p>
                {minVersion && (
                  <p style={{ marginTop: 0, opacity: 0.9 }}>
                    Minimum required version: <strong>{minVersion}</strong>
                  </p>
                )}
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#2563eb',
                    border: 'none',
                    color: '#fff',
                    padding: '10px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Refresh now
                </button>
              </div>
            </div>
          )}
          {/* n8n Chat Widget Target */}
          <div id="n8n-chat"></div>
          <ChatWidget />
          <NotifyProvider />
          <SeoDefaults />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/betfair" element={<LandingPage variant={BETFAIR_LANDING_VARIANT} />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/guide" element={<HelpLayout />}>
              <Route index element={<Navigate to="getting-started" replace />} />
              <Route path="getting-started" element={<GuideGettingStarted />} />
              <Route path="account-subscription" element={<GuideAccountSubscription />} />
              <Route path="betfair-ai-mode" element={<GuideBetfairAiMode />} />
              <Route path="races-and-betting" element={<GuideRacesAndBetting />} />
              <Route path="performance-history" element={<GuidePerformanceHistory />} />
              <Route path="settings-digest" element={<GuideSettingsDigest />} />
              <Route path="troubleshooting" element={<GuideTroubleshooting />} />
            </Route>
            <Route path="/start-checkout" element={<Navigate to="/start-checkout/betfair" replace />} />
            <Route path="/start-checkout/betfair" element={<MarketingCheckoutStart />} />
            <Route path="/resume-signup" element={<ResumeSignupPage />} />
            <Route path="/track-record" element={<PublicTrackRecordPage />} />

            {/* Protected routes */}
            <Route path="/connect-betfair" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <ConnectBetfairPage />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <ConnectBetfairPage />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/today-races" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <TodayRaces />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/results" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <ResultsHistory />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/open-bets" element={
              <ProtectedRoute>
                <Navigate to="/today-bets" replace />
              </ProtectedRoute>
            } />
            <Route path="/today-bets" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <OpenBets />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/performance" element={
              <ProtectedRoute>
                <SubscriptionRoute>
                <Navigation />
                <main className="app-main">
                  <Performance />
                </main>
                <AppFooter />
                </SubscriptionRoute>
              </ProtectedRoute>
            } />
            <Route path="/subscribe" element={
              <ProtectedRoute>
                <Navigation />
                <main className="app-main">
                  <SubscribePage />
                </main>
                <AppFooter />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Navigation />
                <main className="app-main">
                  <Settings />
                </main>
                <AppFooter />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminRoute>
                  <Navigation />
                  <main className="app-main">
                    <AdminPage />
                  </main>
                  <AppFooter />
                </AdminRoute>
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;
